import { createContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate ,useSearchParams} from 'react-router-dom';

import { EN } from '../../Shared/constants/constant';
import { getLangData, getProductConfigData } from '../api';
import { getProductCode, getSessionData } from '../utills';
import { setSessionData } from '../../Shared/helper/storage';
import { CONFIG_DATA_TEXT, LANG_DATA_TEXT ,CONFIG_KEY} from '../GlobalConstant';
import { useLanguage } from '../../Shared/cache/context/LanguageProvider';

export const TransAndConfigContext = createContext();

export const ConfigAndTranslationProvider = ({ children }) => {

  const [searchParams] = useSearchParams();
  const [configData, setConfigData] = useState(() => {
    return getSessionData(CONFIG_DATA_TEXT)
  });
  const [langData, setLangData] = useState(() => {
    return getSessionData(LANG_DATA_TEXT)
  });
  const [pendingRequests, setPendingRequests] = useState(1);


  const navigate = useNavigate();
  const location = useLocation();
  const { selectedLanguage } = useLanguage();

  const productCode = useMemo(() => getProductCode(location?.pathname,searchParams), [location?.pathname,searchParams]);

  const startLoading = () =>
    setPendingRequests(count => count + 1);

  const stopLoading = () =>
    setPendingRequests(count => Math.max(0, count - 1));

  const fetchConfigData = useCallback(async (productCodeValue) => {
    if (!(productCode || productCodeValue))
      return
    startLoading()
    try {
      const configRes = await getProductConfigData(productCodeValue ?? productCode);
      setConfigData(prev => {
        const sessionData = {
          ...prev,
          [productCodeValue ?? productCode]: configRes,
        };
        setSessionData(CONFIG_DATA_TEXT, sessionData)
        return sessionData;
      })
      stopLoading()
    } catch (err) {
      navigate('/');
    }
  }, [productCode, navigate]);

  const fetchLangData = useCallback(async (lang = EN) => {
    startLoading()
    try {
      const headers = {
        "Accept-language": lang
      }
      const langRes = await getLangData(headers);
      setLangData(prev => {
        const updated = { ...prev, [lang]: langRes };
        setSessionData(LANG_DATA_TEXT, updated);
        return updated;
      });
      stopLoading()
    } catch (err) {
      navigate('/')
    }
  }, [navigate]);


  useEffect(() => {
    if (!configData?.[CONFIG_KEY?.GLOBAL_CONFIG]) {
      fetchConfigData(CONFIG_KEY?.GLOBAL_CONFIG);
    }
    // dashboardData.fttxRechargeConfig (new broadband recharge flow) is read on every recharge page, not only the home page
    // (on the home page itself the product config effect below already loads it)
    if (!configData?.[CONFIG_KEY?.HOMEPAGE_CONFIG] && productCode !== CONFIG_KEY?.HOMEPAGE_CONFIG) {
      fetchConfigData(CONFIG_KEY?.HOMEPAGE_CONFIG);
    }
  }, []);

  useEffect(() => {
    const selectedLang = selectedLanguage?.toLowerCase();
    if (!langData?.[selectedLang]) {
      fetchLangData(selectedLang);
    }
  }, [selectedLanguage, fetchLangData])

  useEffect(() => {
    if (!configData?.[productCode]) {
      fetchConfigData();
    } else {
      stopLoading();
    }
  }, [productCode]);

  const contextValue = useMemo(() => {
    return ({
      langData: langData?.[selectedLanguage?.toLowerCase()] ?? {},
      configData: configData?.[productCode] ?? configData,
      loading: pendingRequests > 1,
      globalConfig: configData?.[CONFIG_KEY?.GLOBAL_CONFIG],
      homePageConfig: configData?.[CONFIG_KEY?.HOMEPAGE_CONFIG]
    })
  }, [langData, configData, productCode, selectedLanguage, pendingRequests]);


  return (
    <TransAndConfigContext.Provider
      value={contextValue}
    >
      {children}
    </TransAndConfigContext.Provider>
  );
};